import React, { useState } from 'react';

import classNames from 'classnames';
import UnderProgress from 'icons/under-progress.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import ArchiveButton from 'components/personnel/ArchiveButton';
import PersonnelDetails from 'components/personnel/PersonnelDetails';
import PersonnelManagementDetails from 'components/personnel/PersonnelManagementDetails';
import PersonnelShow from 'components/personnel/PersonnelShow';
import PersonnelTeamDetails from 'components/personnel/PersonnelTeamDetails';
import ShowFooter from 'components/sidepanels/ShowFooter';

import { addDays, asFormattedDateTime } from 'components/helpers/dates';
import { resourceShape } from 'components/helpers/serialisableResources';

const numberOfOngoingProjects = 6;

export default function PersonnelPanel({
  currentPersonnel,
  division,
  isProfilePhotoLoading,
  lineManager,
  onArchiveButtonClick,
  onDeleteClick,
  onSendInvitationClick,
  openSidePanel,
  personnel,
  roles,
  subcontractor,
  teams,
  user,
}) {
  const breadBoard = useBreadBoard();

  const [ongoingProjects, setOngoingProjects] = useState({
    loaded: false,
    collection: [],
    metaData: { totalCount: 0 },
  });

  const [
    wereOngoingProjectsPreviouslyFetched,
    setWereOngoingProjectsPreviouslyFetched,
  ] = useState(false);

  const {
    hasPersonnelEditableAccess,
    hasPersonnelProfileAccessEnabled,
    hasProfileAccessEditableAccess,
    hasTeamViewableAccess,
    isViewingOwnProfile,
  } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelPanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleArchiveButtonMouseEnter = () => {
    if (
      !isArchived &&
      currentPersonnel.isParticipatingInOngoingProjects &&
      !wereOngoingProjectsPreviouslyFetched
    ) {
      setWereOngoingProjectsPreviouslyFetched(true);
      fetchOngoingProjects();
    }
  };

  const fetchOngoingProjects = () => {
    axios
      .get(`/personnel/${currentPersonnel.id}/ongoing_projects`, {
        params: { per: numberOfOngoingProjects },
      })
      .then((response) => {
        setOngoingProjects((prevState) => ({
          loaded: true,
          collection: prevState.collection.concat(response.data.data),
          metaData: response.data.meta,
        }));
      })
      .catch(breadBoard.addInedibleToast);
  };

  const isArchived = !!currentPersonnel.archivedAt;

  const isUserWithProfileAccess =
    user &&
    (user.attributes.accessType !== 'personnel' ||
      user.attributes.profileAccessEnabled);

  const isPersonnelDetailsVisible =
    currentPersonnel.lineManagerId ||
    currentPersonnel.email ||
    currentPersonnel.externalId ||
    teams.length > 0 ||
    Object.keys(currentPersonnel.fieldValues).length > 0;

  const isProfileAccessDetailsVisible =
    !isViewingOwnProfile && hasPersonnelProfileAccessEnabled;

  return (
    <>
      <div className='tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
        <div className='tw-flex tw-justify-center'>
          <div className='tw-mb-6 tw-mt-8 tw-h-20 tw-w-20'>
            {isProfilePhotoLoading ?
              <Tooltip
                placement='bottom'
                tooltip={i18nTCommon('shared.updateInProgress')}
                trigger='hover'
              >
                <div className='tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-grey-200'>
                  <UnderProgress />
                </div>
              </Tooltip>
            : <ProfilePhotoDisplay
                componentSize='lg'
                photoUrl={currentPersonnel?.profilePhoto?.croppedUrl}
                profileInitials={`${currentPersonnel.firstName[0]}${currentPersonnel.lastName[0]}`}
              />
            }
          </div>
        </div>
        {personnel && (
          <PersonnelShow
            division={division}
            isCompact={true}
            personnel={personnel}
            roles={roles}
            subcontractor={subcontractor}
          />
        )}
        {hasPersonnelEditableAccess && (
          <ShowFooter
            isCompact={true}
            isEditButtonHidden={isArchived}
            onDeleteClick={onDeleteClick}
            onEditClick={openSidePanel}
          >
            <ArchiveButton
              disabled={
                !isArchived && currentPersonnel.isParticipatingInOngoingProjects
              }
              disabledTooltip={
                <DisabledArchivingPersonnelToolTip
                  currentPersonnel={currentPersonnel}
                  ongoingProjects={ongoingProjects}
                />
              }
              isArchived={isArchived}
              isCompact={true}
              onClick={onArchiveButtonClick}
              onMouseEnter={handleArchiveButtonMouseEnter}
            />
          </ShowFooter>
        )}
      </div>
      {(isProfileAccessDetailsVisible || isPersonnelDetailsVisible) && (
        <div className='tw-mt-4 tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
          <div className='tw-p-5'>
            <h2 className='tw-m-0 tw-mb-4 tw-text-l tw-font-semibold'>
              {i18nTCommon('personnel.personnelDetails')}
            </h2>
            {isProfileAccessDetailsVisible &&
              (() => {
                if (subcontractor) {
                  return (
                    <ProfileAccessDetails
                      label={i18nTCommon('shared.noAccess')}
                      text={i18nT('access.subcontractor')}
                    />
                  );
                }
                if (personnel && isUserWithProfileAccess) {
                  return (
                    <ProfileAccessStatusDetails
                      isInviteSendable={hasProfileAccessEditableAccess}
                      onSendInvitationClick={onSendInvitationClick}
                      personnel={personnel}
                      user={user}
                    />
                  );
                }
                if (personnel && !personnel.attributes.email) {
                  return (
                    <ProfileAccessDetails
                      label={i18nTCommon('shared.notInvited')}
                      text={i18nT('access.noEmailAddress')}
                    />
                  );
                }
                if (personnel && !isUserWithProfileAccess) {
                  return (
                    <ProfileAccessDetails
                      label={i18nTCommon('shared.notInvited')}
                      text={i18nTCommon('personnel.cantViewProfile')}
                    />
                  );
                }
              })()}
            {isPersonnelDetailsVisible && (
              <PersonnelDetails
                currentPersonnel={currentPersonnel}
                defaultDetails={[
                  {
                    label: i18nTCommon('personnel.personnelId'),
                    text: currentPersonnel.externalId,
                  },
                  {
                    label: i18nTCommon('shared.email'),
                    text: currentPersonnel.email,
                  },
                ]}
                isCompact={true}
              />
            )}
          </div>
        </div>
      )}
      {hasTeamViewableAccess && (
        <div className='overflow-auto tw-mt-4 tw-max-h-[640px] tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
          <div className='tw-mb-6 tw-px-5 tw-pt-6'>
            <h2
              className={classNames(
                'm-0 tw-text-l tw-font-semibold',
                teams.length === 0 ? 'tw-mb-4' : 'tw-mb-6',
              )}
            >
              {i18nTCommon('shared.teams')}
            </h2>
            <PersonnelTeamDetails teams={teams} />
          </div>
        </div>
      )}
      <div className='overflow-auto tw-mt-4 tw-max-h-[640px] tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white'>
        <div className='tw-px-5 tw-pt-6'>
          <h2 className='tw-m-0 tw-mb-4 tw-text-l tw-font-semibold'>
            {i18nTCommon('shared.management')}
          </h2>
          <PersonnelManagementDetails
            currentPersonnel={currentPersonnel}
            lineManager={lineManager}
          />
        </div>
      </div>
    </>
  );
}

PersonnelPanel.propTypes = {
  currentPersonnel: PropTypes.object.isRequired,
  personnel: resourceShape('personnel'),
  user: resourceShape('user'),
  division: resourceShape('company'),
  subcontractor: resourceShape('subcontractor'),
  lineManager: resourceShape('lineManager'),
  teams: PropTypes.arrayOf(resourceShape('team')),
  roles: PropTypes.arrayOf(resourceShape('companyRole')),
  openSidePanel: PropTypes.func.isRequired,
  onArchiveButtonClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onSendInvitationClick: PropTypes.func,
  isProfilePhotoLoading: PropTypes.bool,
};

export function DisabledArchivingPersonnelToolTip({
  currentPersonnel,
  ongoingProjects,
}) {
  const {
    division: {
      attributes: { pluralRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelPanel.DisabledArchivingPersonnelToolTip',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const projectNames = ongoingProjects.collection.map(
    (project) => project.attributes.name,
  );

  const otherCount =
    ongoingProjects.metaData.totalCount - numberOfOngoingProjects;

  return (
    <div className='tw-w-[320px] tw-text-left'>
      <p className='tw-w-[300px]'>
        {i18nT('text', {
          firstName: currentPersonnel.firstName,
          interpolation: { escapeValue: false },
          pluralRAMSName,
        })}
      </p>
      <ul className='tw-m-0 tw-px-3'>
        {projectNames.map((project, index) => (
          <li key={index}>{project}</li>
        ))}
      </ul>
      {otherCount > 0 && (
        <p className='tw-mb-0 tw-mt-3'>
          {i18nTShared('other', { count: otherCount })}
        </p>
      )}
    </div>
  );
}

DisabledArchivingPersonnelToolTip.propTypes = {
  currentPersonnel: PropTypes.object.isRequired,
  ongoingProjects: PropTypes.object.isRequired,
};

export function ProfileAccessDetails({ children, label, text }) {
  return (
    <div className='tw-mb-4'>
      <div className='tw-font-medium tw-text-grey-900'>{label}</div>
      <div>{text}</div>
      {children && <div>{children}</div>}
    </div>
  );
}

ProfileAccessDetails.propTypes = {
  label: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export function ProfileAccessStatusDetails({
  isInviteSendable,
  onSendInvitationClick,
  personnel,
  user,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelPanel.ProfileAccessStatusDetails',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { invitationAcceptedAt, invitationSentAt, invitationStatus } =
    user.attributes;

  const label = (() => {
    if (invitationStatus === 'none') return i18nTCommon('shared.notInvited');

    if (invitationStatus === 'expired') {
      return i18nTCommon('shared.inviteExpired');
    }

    if (['accepted', 'pending'].includes(invitationStatus)) {
      return i18nT(`label.${invitationStatus}`);
    }
  })();

  const text = (() => {
    switch (invitationStatus) {
      case 'accepted':
        return (
          invitationAcceptedAt &&
          asFormattedDateTime({ date: invitationAcceptedAt })
        );
      case 'expired':
        return i18nTCommon('shared.expiredDate', {
          date:
            invitationSentAt &&
            asFormattedDateTime({
              date: addDays({ amount: 10, date: invitationSentAt }),
            }),
        });
      case 'none':
        return i18nTCommon('personnel.cantViewProfile');
      case 'pending':
        return i18nT('text.pending', {
          sentAt:
            invitationSentAt && asFormattedDateTime({ date: invitationSentAt }),
        });
    }
  })();

  return (
    <ProfileAccessDetails label={label} text={text}>
      {isInviteSendable &&
        ['none', 'pending', 'expired'].includes(invitationStatus) && (
          <a
            className='app-link tw-align-baseline tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
            onClick={() => onSendInvitationClick({ personnel })}
          >
            {invitationStatus === 'none' ?
              i18nT('link.send')
            : i18nTCommon('shared.resendInvitation')}
          </a>
        )}
    </ProfileAccessDetails>
  );
}

ProfileAccessStatusDetails.propTypes = {
  isInviteSendable: PropTypes.bool.isRequired,
  onSendInvitationClick: PropTypes.func,
  personnel: resourceShape('personnel').isRequired,
  user: resourceShape('user').isRequired,
};
